import { AlertTriangle, ArrowRight, CheckCircle, Clock, Copy, LogOut, Phone, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { paymentAPI } from '../services/api';
import { responseData } from '../services/apiResponse';
import { clearAuthenticatedUser } from '../services/authStorage';

const Success = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  // const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const pollFailures = useRef(0);

  useEffect(() => {
    let stopped = false;
    let timer;
    const terminalStatuses = new Set(['failed', 'cancelled', 'canceled', 'refunded', 'expired', 'kivi_completed']);
    const schedule = () => {
      const delay = Math.min(120000, 10000 * (2 ** Math.min(pollFailures.current, 3)));
      timer = window.setTimeout(() => void load(false), delay);
    };
    const load = async (initial) => {
      if (stopped || (!initial && document.visibilityState !== 'visible')) {
        if (!stopped) schedule();
        return;
      }
      try {
        if (initial) setLoading(true);
        const response = await paymentAPI.getPaymentStatus(transactionId);
        const nextTransaction = responseData(response);
        setTransaction(nextTransaction);
        pollFailures.current = 0;
        if (!terminalStatuses.has(String(nextTransaction?.status || '').toLowerCase())) schedule();
      } catch (err) {
        pollFailures.current += 1;
        console.error('Failed to load transaction status:', err);
        schedule();
      } finally {
        if (initial) setLoading(false);
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(timer);
      void load(false);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    void load(true);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [transactionId]);

  // const loadIntent = async (requestId) => {
  //   try {
  //     if (!requestId) return;
  //     const response = await intentAPI.getIntent(requestId);
  //     setIntent(responseData(response));
  //   } catch (err) {
  //     console.error('Failed to load intent:', err);
  //   }
  // };

  const handleCopyPhone = () => {
    const phone = transaction?.receiver_phone;
    if (phone) {
      navigator.clipboard.writeText(phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCallPhone = () => {
    const phone = transaction?.receiver_phone;
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  const getStatusColor = (readableStatus) => {
    switch (readableStatus) {
      case 'Transfer Completed': return 'bg-green-100 text-green-800';
      case 'KYC Pending':
      case 'Payment Pending': return 'bg-orange-100 text-orange-800';
      case 'Transfer Processing': return 'bg-yellow-100 text-yellow-800';
      case 'Failed':
      case 'Cancelled':
      case 'Refunded':
      case 'Expired': return 'bg-red-100 text-red-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusInstruction = (readableStatus) => {
    switch (readableStatus) {
      case 'KYC Pending': return 'Your KYC has not been completed yet or not started the payment, check your mailbox and click to resume the KYC/Payment flow.';
      case 'Payment Pending': return 'Your payment has not been completed yet, check your mailbox and click to resume the payment flow.';
      case 'Transfer Processing': return 'We received your payment and are working on transferring it to your recipient.' +
        '\nThe transfer may take a few minutes to complete. Do not leave this page.';
      case 'Transfer Completed': return 'All good, the transfer has been sent successfully to your recipient.' +
        '\nAn email confirmation was sent to you. You may exit this page.';
      case 'Failed': return 'The payment has failed. Please try again or contact support.';
      case 'Cancelled': return 'The payment was cancelled.';
      case 'Refunded': return 'The payment has been refunded to your account.';
      case 'Expired': return 'The payment session has expired. Please start a new payment.';
      default: return '';
    }
  };

  const getPageTitle = (readableStatus) => {
    switch (readableStatus) {
      case 'Transfer Completed': return 'Transfer Completed!';
      case 'Transfer Processing': return 'Transfer Processing';
      case 'KYC Pending': return 'KYC Pending';
      case 'Payment Pending': return 'Payment Pending';
      case 'Failed': return 'Payment Failed';
      case 'Cancelled': return 'Payment Cancelled';
      case 'Refunded': return 'Payment Refunded';
      case 'Expired': return 'Payment Expired';
      default: return 'Transaction Status';
    }
  };

  const getPageIcon = (readableStatus) => {
    switch (readableStatus) {
      case 'Transfer Completed': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'Transfer Processing': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'KYC Pending':
      case 'Payment Pending': return <Clock className="w-5 h-5 text-orange-500" />;
      case 'Failed':
      case 'Cancelled':
      case 'Refunded':
      case 'Expired': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      default: return <Clock className="w-5 h-5 text-blue-600" />;
    }
  };

  const getStatusBarStyle = (readableStatus) => {
    switch (readableStatus) {
      case 'Transfer Completed': return 'bg-green-50 border-green-200';
      case 'Transfer Processing': return 'bg-yellow-50 border-yellow-200';
      case 'KYC Pending':
      case 'Payment Pending': return 'bg-orange-50 border-orange-200';
      case 'Failed':
      case 'Cancelled':
      case 'Refunded':
      case 'Expired': return 'bg-red-50 border-red-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  const getPaymentAction = () => {
    const s = transaction?.status?.toLowerCase();
    if (['payment_pending', 'order_created'].includes(s)) return 'continue';
    if (['failed', 'cancelled', 'expired', 'refunded'].includes(s)) return 'restart';
    return null;
  };

  const handlePaymentAction = () => {
    // Use request_id from the loaded transaction, not localStorage (which may be stale)
    const requestId = transaction?.request_id || localStorage.getItem('request_id');
    if (!requestId) return;

    // Restore localStorage values needed by TransakCheckout
    localStorage.setItem('request_id', requestId);
    const purposeCode = transaction?.transaction?.purpose_code_sender || localStorage.getItem('purpose_code') || '';
    const confirmedAmount = transaction?.transaction?.confirmed_crypto_amount || localStorage.getItem('confirmed_amount') || '';
    if (purposeCode) localStorage.setItem('purpose_code', purposeCode);
    if (confirmedAmount) localStorage.setItem('confirmed_amount', String(confirmedAmount));

    const countryAlpha2 = transaction?.sender_country_code_alpha2;
    const countryAlpha3 = transaction?.sender_country_code_alpha3;
    if (countryAlpha2) {
      localStorage.setItem('selected_country', countryAlpha2);
      localStorage.setItem('selected_country_alpha2', countryAlpha2);
    }
    if (countryAlpha3) localStorage.setItem('selected_country_alpha3', countryAlpha3);

    navigate(`/review/${encodeURIComponent(requestId)}`);
  };

  const handleLogout = () => {
    clearAuthenticatedUser({ includeSession: true });
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <Layout step={5} totalSteps={5} onBack={() => navigate(-1)}>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading transaction details…</p>
        </div>
      </Layout>
    );
  }

  const readableStatus = transaction?.readable_status || 'Transfer Processing';
  const isProcessing = transaction?.status?.toLowerCase() === 'processing' || readableStatus === 'Transfer Processing';
  const paymentAction = getPaymentAction();

  return (
    <Layout
      step={5}
      totalSteps={5}
      onBack={() => (
        transaction?.request_id
          ? navigate(`/review/${encodeURIComponent(transaction.request_id)}`)
          : navigate(-1)
      )}
    >
      <div className="space-y-4">
        {/* Status bar */}
        <div className={`rounded-lg border p-2 ${getStatusBarStyle(readableStatus)}`}>
          <div className="flex items-center gap-2 mb-1">
            {getPageIcon(readableStatus)}
            <h2 className="text-lg font-bold text-gray-900">{getPageTitle(readableStatus)}</h2>
          </div>
          <p className="text-sm text-gray-600 ml-7 leading-6">{getStatusInstruction(readableStatus)}</p>
          {isProcessing && (
            <div className="ml-7 mt-3 flex items-center gap-2 text-sm font-medium text-yellow-700">
              <img
                src="/assets/DualBall-1x-1.0s-200px-200px.svg"
                alt="Processing"
                className="h-10 w-10 object-contain"
              />
              <span>Processing your transfer…</span>
            </div>
          )}
        </div>

        {/* Transaction card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {/* Amount hero */}
          <div className="text-center py-5 border-b border-gray-100">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Amount</p>
            <span className="text-3xl font-bold text-gray-900 inline-flex items-center gap-2">
              {transaction?.transaction?.confirmed_crypto_amount || '0'} USD
              <img
                src={`/assets/tokens/${(transaction?.settlement?.asset || 'usdc').toLowerCase()}.png`}
                alt={transaction?.settlement?.asset || 'USDC'}
                className="w-7 h-7 rounded-full object-contain"
              />
            </span>
          </div>

          {/* Detail rows */}
          <div className="divide-y divide-gray-100">
            <div className="flex justify-between items-center px-5 py-3">
              <span className="text-sm text-gray-500">Receiver</span>
              <span className="text-sm font-semibold text-gray-900">
                {transaction?.beneficiary?.full_name_receiver}
              </span>
            </div>

            {(transaction?.receiver_phone) && (
              <div className="flex justify-between items-center px-5 py-3">
                <span className="text-sm text-gray-500">Phone</span>
                <div className="flex items-center space-x-1.5">
                  <span className="text-sm font-semibold text-gray-900">{transaction?.receiver_phone}</span>
                  <button
                    onClick={handleCopyPhone}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title="Copy phone"
                  >
                    <Copy className={`w-3.5 h-3.5 ${copied ? 'text-green-600' : 'text-gray-400'}`} />
                  </button>
                  <button
                    onClick={handleCallPhone}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title="Call phone"
                  >
                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center px-5 py-3">
              <span className="text-sm text-gray-500">Purpose</span>
              <span className="text-sm text-gray-900 capitalize">
                {(transaction?.transaction?.purpose_code_sender || transaction?.transaction?.reason_for_sending || '')?.replace(/_/g, ' ')}
              </span>
            </div>

            <div className="flex justify-between items-center px-5 py-3">
              <span className="text-sm text-gray-500">Status</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(readableStatus)}`}>
                {readableStatus}
              </span>
            </div>

            <div className="flex justify-between items-center px-5 py-3">
              <span className="text-sm text-gray-500">Time</span>
              <span className="text-sm text-gray-700">{transaction?.created_at ? new Date(transaction.created_at).toLocaleString() : new Date().toLocaleString()}</span>
            </div>

            {transaction?.request_id && (
              <div className="flex justify-between items-center px-5 py-3">
                <span className="text-sm text-gray-500">Request ID</span>
                <span className="font-mono text-xs text-gray-500 break-all text-right max-w-[60%]">{transaction.request_id}</span>
              </div>
            )}

            <div className="flex justify-between items-center px-5 py-3">
              <span className="text-sm text-gray-500">Transaction ID</span>
              <span className="font-mono text-xs text-gray-500 break-all text-right max-w-[60%]">{transactionId}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          {paymentAction && (
            <button
              onClick={handlePaymentAction}
              className="w-full flex items-center justify-center py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold text-sm"
            >
              {paymentAction === 'continue'
                ? <ArrowRight className="w-4 h-4 mr-2" />
                : <RefreshCw className="w-4 h-4 mr-2" />}
              {paymentAction === 'continue' ? 'Continue Payment' : 'Restart Payment'}
            </button>
          )}

          {/* <button
            onClick={handleSMSNotify}
            disabled={!receiverPhone}
            className={`w-full flex items-center justify-center py-3 border rounded-lg transition-colors font-semibold text-sm ${
              receiverPhone
                ? 'border-primary-600 text-primary-600 hover:bg-primary-50'
                : 'border-gray-300 text-gray-400 cursor-not-allowed'
            }`}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Notify Receiver By SMS
          </button> */}
        </div>

        {/* What's next */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-1.5">What's Next?</h3>
          <ul className="space-y-1 text-xs text-gray-600">
            <li>- The receiver will get the funds once your payment is confirmed</li>
            <li>- You can track this transaction from your Home page</li>
            <li>- Bookmark this page to check status later</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="text-center space-y-2 pt-1">
          <button
            onClick={handleLogout}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center"
          >
            <LogOut className="w-3.5 h-3.5 mr-1" />
            Log Out
          </button>
          <p className="text-xs text-gray-400">For support, contact support@koinremit.com</p>
          <p className="text-xs text-gray-400">
            A service by Kivi3 Technologies Ltd — Licensed by Dubai International Financial Centre.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Success;
